import React from "react";

import { StudentCountsInShapeColorByEnum } from "@/providers/store/api/shapes/enums/student-counts-in-shape.enum";

import { type ISelectionPayload } from "../../map-panel-context";

import { ClusterSelectionSummarySkeleton } from "./cluster-selection/cluster-selection-summary-skeleton";
import { CombineSelectedBoundariesButton } from "./combine-selected-boundaries-button";
import { SectionTitle } from "./section-title";
import { SelectedArea } from "./selected-area";
import { SkeletonBox } from "./skeleton-box";
import StudentBy from "./student-by";
import { STUDENT_BY_TITLE_DICTIONARY } from "./student-by.constants";
import { Summary } from "./summary";

interface SelectionPanelContentProps {
  payload: ISelectionPayload | null;
  isCountsLoading?: boolean;
}

function getPlottingSkeletonMetricCount(
  colorBy: StudentCountsInShapeColorByEnum,
): 3 | 4 {
  return colorBy === StudentCountsInShapeColorByEnum.GRADE ? 4 : 3;
}

function SelectionCountsSkeleton({
  useSchoolCountsOnly,
  colorBy,
}: {
  useSchoolCountsOnly: boolean;
  colorBy: StudentCountsInShapeColorByEnum;
}) {
  const showStudentBySkeleton =
    !useSchoolCountsOnly && colorBy !== StudentCountsInShapeColorByEnum.NONE;

  return (
    <>
      <ClusterSelectionSummarySkeleton
        metricCount={
          useSchoolCountsOnly ? 1 : getPlottingSkeletonMetricCount(colorBy)
        }
        studentsFullWidth={
          !useSchoolCountsOnly && colorBy !== StudentCountsInShapeColorByEnum.GRADE
        }
      />

      {showStudentBySkeleton ? (
        <div>
          <SectionTitle title={STUDENT_BY_TITLE_DICTIONARY[colorBy]} />
          <div className="mt-2 space-y-1">
            <div className="flex gap-2">
              <SkeletonBox className="h-4 w-16 shrink-0" />
              <SkeletonBox className="h-4 flex-1" />
              <SkeletonBox className="h-4 w-12 shrink-0" />
            </div>
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="flex gap-2"
              >
                <SkeletonBox className="h-4 w-20 shrink-0" />
                <SkeletonBox className="h-4 flex-1" />
                <SkeletonBox className="h-4 w-10 shrink-0" />
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </>
  );
}

const SelectionPanelContent: React.FC<SelectionPanelContentProps> = React.memo(
  ({ payload, isCountsLoading = false }) => {
    const useSchoolCountsOnly = payload?.useSchoolCountsOnly ?? true;
    const colorBy = payload?.colorBy ?? StudentCountsInShapeColorByEnum.NONE;
    const showSelectedArea = !payload?.isDistrictWide && (payload?.shapes?.length ?? 0) > 0;

    return (
      <div className="space-y-6 px-3 py-4">
        {showSelectedArea ? (
          <>
            <SelectedArea shapes={payload?.shapes ?? []} />
            <CombineSelectedBoundariesButton />
          </>
        ) : null}
        {isCountsLoading ? (
          <SelectionCountsSkeleton
            colorBy={colorBy}
            useSchoolCountsOnly={useSchoolCountsOnly}
          />
        ) : (
          <>
            <Summary
              dataCount={payload?.dataCount ?? 0}
              schoolCountsOnly={useSchoolCountsOnly}
              schoolsCount={payload?.schoolsCount ?? 0}
              schoolsPlotted={payload?.schoolsPlotted ?? 0}
              studentsCount={payload?.studentsCount ?? 0}
              type={colorBy}
            />
            {!useSchoolCountsOnly ? (
              <StudentBy
                data={payload?.dataBy ?? []}
                type={colorBy}
              />
            ) : null}
          </>
        )}
      </div>
    );
  },
);

export default SelectionPanelContent;
